import React from 'react';
import { Rocket, Users, Bus, AlertTriangle, ArrowRight, Check, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export const LogicielsDeploiement = () => {
  const softwares = [
    {
      id: 'innovagest',
      name: 'Innovagest',
      tagline: 'Gestion RH scolaire',
      description: 'Solution complète de gestion des ressources humaines et du remplacement pour le secteur scolaire québécois.',
      icon: <Users className="w-6 h-6" />,
      gradient: 'from-blue-500 to-cyan-500',
      features: [
        'Module Absence',
        'Module Remplacement',
        'Sync-Intégration GRH'
      ],
      stats: [
        { value: '3', label: 'Modules clés' },
        { value: '100%', label: 'En français' },
        { value: '2026', label: 'Opérationnel' }
      ]
    },
    {
      id: 'innova-transport',
      name: 'Innova-Transport',
      tagline: 'Gestion du transport scolaire',
      description: 'Plateforme SaaS centralisant toutes les demandes de transport scolaire, conforme à la Loi 25 et hébergée au Canada.',
      icon: <Bus className="w-6 h-6" />,
      gradient: 'from-yellow-500 to-orange-500',
      features: [
        'Billetterie centralisée',
        'Priorisation automatisée',
        'Assistant intelligent'
      ],
      stats: [
        { value: '4', label: 'Portails dédiés' },
        { value: 'Loi 25', label: 'Conforme' },
        { value: '24/7', label: 'Disponibilité' }
      ]
    },
    {
      id: 'gestevent',
      name: 'Gestevent',
      tagline: 'Gestion des événements VI/VACS',
      description: 'Plateforme numérique de gestion des événements de violence et d\'intimidation pour les milieux scolaires.',
      icon: <AlertTriangle className="w-6 h-6" />,
      gradient: 'from-red-500 to-pink-500',
      features: [
        'Réception de signalements',
        'Journalisation complète',
        'Rapports automatisés'
      ],
      stats: [
        { value: '100%', label: 'Confidentiel' },
        { value: 'Cloud', label: 'Hébergement' },
        { value: 'RGPD', label: 'Sécurisé' }
      ]
    }
  ];

  return (
    <section id="logiciels-deploiement" className="py-24 relative overflow-hidden bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
      {/* Background Effects */}
      <div className="absolute inset-0 network-lines opacity-5"></div>
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#FF6B35] opacity-10 blur-[150px] rounded-full"></div>
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-purple-500 opacity-10 blur-[150px] rounded-full"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B35] mb-4 block">
            Prêts à déployer
          </span>
          <h2 className="text-4xl sm:text-5xl font-black mb-6 tracking-tight">
            Logiciels <span className="text-[#FF6B35]">Clés en Main</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            Des solutions complètes, éprouvées et prêtes pour le déploiement. Conçues spécifiquement pour le secteur scolaire québécois.
          </p>
        </motion.div>

        {/* Software Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {softwares.map((software, index) => (
            <motion.div
              key={software.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-[#121212] border border-white/10 rounded-2xl overflow-hidden hover:border-[#FF6B35]/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(255,107,53,0.2)] flex flex-col h-full"
            >
              {/* Card Header with Gradient */}
              <div className={`relative p-6 bg-gradient-to-br ${software.gradient} bg-opacity-10`}>
                <div className="absolute inset-0 bg-black/40"></div>
                <div className="relative z-10">
                  <div className={`w-14 h-14 bg-gradient-to-br ${software.gradient} rounded-xl flex items-center justify-center mb-4 text-white shadow-lg`}>
                    {software.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-1">{software.name}</h3>
                  <p className="text-sm text-gray-300">{software.tagline}</p>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6 flex-grow flex flex-col">
                <p className="text-gray-400 mb-6 flex-grow">
                  {software.description}
                </p>

                {/* Features */}
                <div className="space-y-2 mb-6">
                  {software.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <div className="w-5 h-5 bg-[#FF6B35]/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-[#FF6B35]" />
                      </div>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mb-6 pt-6 border-t border-white/10">
                  {software.stats.map((stat, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-lg font-bold text-[#FF6B35]">{stat.value}</div>
                      <div className="text-xs text-gray-500">{stat.label}</div>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full bg-[#FF6B35] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] inline-flex items-center justify-center gap-2"
                >
                  Demander une démo
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action - Detailed Page Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center glass rounded-xl p-8 border border-white/10"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Rocket className="w-8 h-8 text-[#FF6B35]" />
            <h3 className="text-2xl font-bold">Découvrez nos solutions en détail</h3>
          </div>
          <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
            Explorez les fonctionnalités complètes, les cas d&apos;usage et les avantages de chaque logiciel.
          </p>
          <Link
            to="/logiciels-deploiement"
            className="inline-flex items-center gap-2 bg-transparent border-2 border-[#FF6B35] text-[#FF6B35] font-semibold px-8 py-3 rounded-lg hover:bg-[#FF6B35] hover:text-white transition-all"
          >
            Voir tous les détails
            <ExternalLink className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
